/**
 * Onde cada tile do acervo mora dentro das folhas.
 *
 * Traduz um número de tile para os retângulos que precisam ser copiados. Um
 * tile comum é um retângulo só; um autotile são quatro quadrantes de meio tile,
 * escolhidos pela forma. A lógica é a do acervo, e as faixas de ID vêm de lá —
 * é o que garante que o mapa desenhado aqui seja o mesmo que o jogo original
 * desenhava.
 */

import {
  FLOOR_AUTOTILE_TABLE,
  WALL_AUTOTILE_TABLE,
  WATERFALL_AUTOTILE_TABLE,
  type ShapeTable,
} from './autotile-tables.js';

export const TILE_SIZE = 48;
export const HALF_TILE = TILE_SIZE / 2;

export const TILE_ID_A5 = 1536;
export const TILE_ID_A1 = 2048;
export const TILE_ID_A2 = 2816;
export const TILE_ID_A3 = 4352;
export const TILE_ID_A4 = 5888;
export const TILE_ID_MAX = 8192;

/**
 * Número da folha dentro do tileset.
 * 0–3 são os autotiles A1–A4, 4 é a A5, e 5–8 são B, C, D e E.
 */
export type SheetNumber = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface TilePiece {
  readonly sheet: SheetNumber;
  readonly sx: number;
  readonly sy: number;
  readonly sw: number;
  readonly sh: number;
  readonly dx: number;
  readonly dy: number;
}

export function isVisible(tileId: number): boolean {
  return tileId > 0 && tileId < TILE_ID_MAX;
}

export function isAutotile(tileId: number): boolean {
  return tileId >= TILE_ID_A1;
}

export function autotileKind(tileId: number): number {
  return Math.floor((tileId - TILE_ID_A1) / 48);
}

export function autotileShape(tileId: number): number {
  return (tileId - TILE_ID_A1) % 48;
}

export function isTileA1(tileId: number): boolean {
  return tileId >= TILE_ID_A1 && tileId < TILE_ID_A2;
}

export function isTileA2(tileId: number): boolean {
  return tileId >= TILE_ID_A2 && tileId < TILE_ID_A3;
}

export function isTileA3(tileId: number): boolean {
  return tileId >= TILE_ID_A3 && tileId < TILE_ID_A4;
}

export function isTileA4(tileId: number): boolean {
  return tileId >= TILE_ID_A4 && tileId < TILE_ID_MAX;
}

export function isTileA5(tileId: number): boolean {
  return tileId >= TILE_ID_A5 && tileId < TILE_ID_A1;
}

/** Tiles de "mesa": a fileira que ganha uma borda rebaixada por cima. */
function isTableTile(tileId: number): boolean {
  // No acervo, a marca de mesa vem de uma opção do tileset que não é usada em
  // nenhum dos seis. Fica declarado para quando alguém a usar no editor.
  return false;
}

/** Um tile comum: um retângulo de tamanho inteiro, copiado direto. */
function normalTile(tileId: number, dx: number, dy: number): TilePiece[] {
  const sheet = (isTileA5(tileId) ? 4 : 5 + Math.floor(tileId / 256)) as SheetNumber;
  const sx = ((Math.floor(tileId / 128) % 2) * 8 + (tileId % 8)) * TILE_SIZE;
  const sy = (Math.floor((tileId % 256) / 8) % 16) * TILE_SIZE;

  return [{ sheet, sx, sy, sw: TILE_SIZE, sh: TILE_SIZE, dx, dy }];
}

/**
 * Um autotile: quatro quadrantes escolhidos pela forma.
 *
 * `frame` anima a superfície da água — o acervo alterna entre três posições
 * numa ida e volta, e é o que faz um lago parecer vivo em vez de estampado.
 */
function autotilePieces(tileId: number, dx: number, dy: number, frame: number): TilePiece[] {
  const kind = autotileKind(tileId);
  const shape = autotileShape(tileId);
  const tx = kind % 8;
  const ty = Math.floor(kind / 8);

  let sheet: SheetNumber = 0;
  let bx = 0;
  let by = 0;
  let table: ShapeTable = FLOOR_AUTOTILE_TABLE;

  if (isTileA1(tileId)) {
    const surface = [0, 1, 2, 1][frame % 4] ?? 0;
    sheet = 0;
    if (kind === 0) {
      bx = surface * 2;
      by = 0;
    } else if (kind === 1) {
      bx = surface * 2;
      by = 3;
    } else if (kind === 2) {
      bx = 6;
      by = 0;
    } else if (kind === 3) {
      bx = 6;
      by = 3;
    } else {
      bx = Math.floor(tx / 4) * 8;
      by = ty * 6 + (Math.floor(tx / 2) % 2) * 3;
      if (kind % 2 === 0) {
        bx += surface * 2;
      } else {
        bx += 6;
        table = WATERFALL_AUTOTILE_TABLE;
        by += frame % 3;
      }
    }
  } else if (isTileA2(tileId)) {
    sheet = 1;
    bx = tx * 2;
    by = (ty - 2) * 3;
  } else if (isTileA3(tileId)) {
    sheet = 2;
    bx = tx * 2;
    by = (ty - 6) * 2;
    table = WALL_AUTOTILE_TABLE;
  } else if (isTileA4(tileId)) {
    sheet = 3;
    bx = tx * 2;
    by = Math.floor((ty - 10) * 2.5 + (ty % 2 === 1 ? 0.5 : 0));
    if (ty % 2 === 1) table = WALL_AUTOTILE_TABLE;
  }

  const quadrants = table[shape] ?? table[0]!;
  const pieces: TilePiece[] = [];

  for (let i = 0; i < 4; i++) {
    const q = quadrants[i];
    if (!q) continue;
    pieces.push({
      sheet,
      sx: (bx * 2 + q[0]) * HALF_TILE,
      sy: (by * 2 + q[1]) * HALF_TILE,
      sw: HALF_TILE,
      sh: HALF_TILE,
      dx: dx + (i % 2) * HALF_TILE,
      dy: dy + Math.floor(i / 2) * HALF_TILE,
    });
  }

  return pieces;
}

/** Os pedaços a desenhar para um tile, na posição pedida. */
export function tilePieces(tileId: number, dx: number, dy: number, frame = 0): TilePiece[] {
  if (!isVisible(tileId)) return [];
  return isAutotile(tileId)
    ? autotilePieces(tileId, dx, dy, frame)
    : normalTile(tileId, dx, dy);
}

/** A água anima; o resto do mapa é estático e pode ser desenhado uma vez só. */
export function isAnimated(tileId: number): boolean {
  return isTileA1(tileId);
}

export { isTableTile };
